import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  LogOut, Newspaper, Home, Megaphone, FileText, Save, Eye, Mail, Phone, MapPin, Inbox, Settings as SettingsIcon,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { pagesAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';

const PAGE_META = {
  'hakkimizda': {
    label: 'Hakkımızda',
    description: 'Site ziyaretçilerine kurumsal kimliğinizi anlatan içeriği yönetin.',
    publicPath: '/hakkimizda',
    showContact: false,
  },
  'reklam-isbirligi': {
    label: 'Reklam ve İş Birliği',
    description: 'Reklam veren ve iş ortakları için açıklama ve iletişim bilgileri.',
    publicPath: '/reklam-isbirligi',
    showContact: true,
  },
};

const AdminPageEditor = () => {
  const { slug } = useParams();
  const meta = PAGE_META[slug];
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    title: '', body: '', contact_email: '', contact_phone: '', contact_address: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/admin/giris');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !meta) return;
    setLoading(true);
    pagesAPI.get(slug)
      .then((res) => {
        const d = res.data || {};
        setForm({
          title: d.title || '',
          body: d.body || '',
          contact_email: d.contact_email || '',
          contact_phone: d.contact_phone || '',
          contact_address: d.contact_address || '',
        });
      })
      .catch(() => toast.error('Sayfa içeriği yüklenemedi'))
      .finally(() => setLoading(false));
  }, [isAuthenticated, slug, meta]);

  if (!meta) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <p className="text-gray-500">Sayfa bulunamadı.</p>
      </div>
    );
  }

  const handleChange = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { title: form.title, body: form.body };
      if (meta.showContact) {
        payload.contact_email = form.contact_email;
        payload.contact_phone = form.contact_phone;
        payload.contact_address = form.contact_address;
      }
      await pagesAPI.update(slug, payload);
      toast.success('Değişiklikler kaydedildi ve canlıya yansıdı');
    } catch (e) {
      toast.error('Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/giris');
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-gray-500">Yükleniyor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid={`admin-page-${slug}`}>
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0F172A] text-white p-4 z-50">
        <div className="mb-8">
          <h1 className="text-lg font-bold tracking-tight">HABER TÜRKİYE'DEN</h1>
          <p className="text-gray-400 text-xs mt-1">Yönetim Paneli</p>
        </div>

        <nav className="space-y-1">
          <Link to="/admin" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <Newspaper className="w-5 h-5" /><span>Haberler</span>
          </Link>
          <Link to="/admin/talepler" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <Inbox className="w-5 h-5" /><span>Gelen Talepler</span>
          </Link>
          <Link to="/admin/reklamlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <Megaphone className="w-5 h-5" /><span>Reklam Yönetimi</span>
          </Link>
          <Link to="/admin/ayarlar" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
            <SettingsIcon className="w-5 h-5" /><span>Site Ayarları</span>
          </Link>

          <div className="pt-4 pb-1">
            <p className="px-3 text-xs uppercase font-bold text-gray-500 tracking-wider">Sayfa Yönetimi</p>
          </div>
          <Link
            to="/admin/sayfa/hakkimizda"
            data-testid="admin-nav-page-hakkimizda"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              slug === 'hakkimizda' ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
          >
            <FileText className="w-5 h-5" /><span>Hakkımızda</span>
          </Link>
          <Link
            to="/admin/sayfa/reklam-isbirligi"
            data-testid="admin-nav-page-reklam-isbirligi"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors ${
              slug === 'reklam-isbirligi' ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'
            }`}
          >
            <FileText className="w-5 h-5" /><span>Reklam & İş Birliği</span>
          </Link>

          <div className="pt-4">
            <Link to="/" className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Home className="w-5 h-5" /><span>Siteyi Görüntüle</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="border-t border-gray-700 pt-4 mb-4">
            <p className="text-gray-400 text-sm">{user?.username}</p>
          </div>
          <Button variant="ghost" className="w-full justify-start text-gray-400 hover:text-white hover:bg-white/5" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Çıkış Yap
          </Button>
        </div>
      </aside>

      <main className="ml-64 p-8 max-w-5xl">
        <div className="flex items-start justify-between mb-8 gap-4 flex-wrap">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{meta.label}</h2>
            <p className="text-gray-500 text-sm mt-1">{meta.description}</p>
          </div>
          <div className="flex gap-2">
            <Link to={meta.publicPath} target="_blank">
              <Button variant="outline" size="sm" data-testid="page-preview-btn">
                <Eye className="w-4 h-4 mr-2" /> Önizle
              </Button>
            </Link>
            <Button
              onClick={handleSave}
              disabled={saving || loading}
              className="bg-[#0284C7] hover:bg-[#0369A1]"
              data-testid="page-save-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Kaydediliyor…' : 'Kaydet'}
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center text-gray-500 py-16">Yükleniyor…</div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-5">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Sayfa Başlığı</label>
              <Input
                value={form.title}
                onChange={handleChange('title')}
                placeholder={meta.label}
                data-testid="page-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">İçerik</label>
              <Textarea
                value={form.body}
                onChange={handleChange('body')}
                rows={meta.showContact ? 12 : 18}
                className="font-mono text-sm leading-relaxed"
                placeholder="İki paragrafı boş satırla ayırın. Markdown stilinde * **kalın** ve _italik_ desteklenir."
                data-testid="page-body-textarea"
              />
              <p className="text-xs text-gray-400 mt-1">
                Boş satırlar paragraf, **kalın**, *italik*, ve - liste işaretleri otomatik biçimlendirilir.
              </p>
            </div>

            {meta.showContact && (
              <div className="border-t border-gray-100 pt-5">
                <h3 className="font-semibold text-gray-900 mb-1">İletişim Bilgileri</h3>
                <p className="text-xs text-gray-500 mb-4">İş ortakları için iletişim kanalları (boş bırakılabilir).</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      <Mail className="w-4 h-4 inline mr-1" /> E-posta
                    </label>
                    <Input
                      type="email"
                      value={form.contact_email}
                      onChange={handleChange('contact_email')}
                      placeholder="reklam@haberturkiyeden.com"
                      data-testid="page-contact-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      <Phone className="w-4 h-4 inline mr-1" /> Telefon
                    </label>
                    <Input
                      value={form.contact_phone}
                      onChange={handleChange('contact_phone')}
                      placeholder="+90 212 000 00 00"
                      data-testid="page-contact-phone"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      <MapPin className="w-4 h-4 inline mr-1" /> Adres
                    </label>
                    <Input
                      value={form.contact_address}
                      onChange={handleChange('contact_address')}
                      placeholder="İstanbul, Türkiye"
                      data-testid="page-contact-address"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPageEditor;
